// ═══════════════════════════════════════════════════════════════════════════
// maturityModel.js — Maturity × Complexity → Application Configuration
// ═══════════════════════════════════════════════════════════════════════════
// The user picks a MATURITY level (L0–L5) and a COMPLEXITY level (1–5).
// This module maps that pair to a complete application profile:
//   · which architecture option (1–15) is recommended
//   · which tabs are visible
//   · which interface GROUPS are in scope
//   · which capabilities/features are unlocked
//   · the headline message + colour theme accent
//
// Design principle: lower maturity/complexity = fewer tabs, simpler language,
// fewer in-scope interfaces. Higher = the full federated/agentic surface.
// This keeps the UI honest — you only see what your maturity can support.
// ═══════════════════════════════════════════════════════════════════════════

// ─── 1 · MATURITY LEVELS (CMMI-style ladder) ───────────────────────────────
export const MATURITY_LEVELS = {
  0: {
    id: 0, key: "L0", label: "Ad hoc",
    color: "#64b5d6",
    tagline: "Heroics, email, spreadsheets. No repeatable process.",
    description: "Integration happens by manual re-keying and weekend CSV exports. No audit trail, no canonical data.",
  },
  1: {
    id: 1, key: "L1", label: "Defined",
    color: "#00d4c0",
    tagline: "Some integrations exist, but each is a snowflake.",
    description: "First real point-to-point connections. Per-system ownership; reconciliation is manual.",
  },
  2: {
    id: 2, key: "L2", label: "Managed",
    color: "#00e5ff",
    tagline: "Repeatable pattern; ownership is clear.",
    description: "Vendor gateways or a hub/iPaaS in place. SLAs starting to be documented.",
  },
  3: {
    id: 3, key: "L3", label: "Standardized",
    color: "#38a8ff",
    tagline: "Canonical contracts, vendor suite, documented SLAs.",
    description: "Three-tier CLM (PLM↔MES↔ERP) with gateways enforcing canonical models. The classic CLM go-live.",
  },
  4: {
    id: 4, key: "L4", label: "Quantitative",
    color: "#c084fc",
    tagline: "Event-driven, measured, federation contracts enforced.",
    description: "Kafka/event-log or B2MML/ISA-95 federation. Latency, error-rate and completeness are measured.",
  },
  5: {
    id: 5, key: "L5", label: "Optimizing",
    color: "#ff4f8b",
    tagline: "Continuous improvement loops; AI / digital-twin feedback.",
    description: "Agentic orchestration, data mesh, or digital thread. The loop closes autonomously with human-in-loop gates.",
  },
};

// ─── 1b · DEEP GUIDANCE per maturity level ─────────────────────────────────
// Everything a decision-maker needs to self-assess and choose correctly.
// Each entry answers: "Is this us? Should we pick this? What do we get/risk?"
//
export const MATURITY_GUIDANCE = {
  0: {
    oneLiner: "We move data by hand. Nothing is connected.",
    plainEnglish: "Your systems don't talk to each other. People copy data between PLM, ERP and the shop floor by hand, by email, or with spreadsheets. Engineering changes take weeks to reach the line because someone has to re-key them.",
    chooseWhen: [
      "You have one plant and fewer than ~100 active products",
      "There is no integration software in place at all today",
      "You are documenting the current state before any project starts",
      "Your budget for integration this year is effectively zero",
    ],
    avoidWhen: [
      "You already have any working system-to-system connection",
      "You are in a regulated industry that requires audit trails",
      "Manual re-keying errors are already costing you scrap or recalls",
    ],
    realWorldSignals: [
      "\"Where's the latest BOM?\" gets answered with \"check the shared drive\"",
      "Engineers email Excel files of routings to production",
      "Month-end reconciliation is a manual, multi-day effort",
      "No one can tell you, in real time, what's running on the line",
    ],
    whatYouGet: "A readiness tracker. The platform helps you document interfaces you'll eventually build — but nothing is in scope yet because nothing is integrated.",
    typicalOrg: "Small job-shop, early-stage manufacturer, or a plant that has never run an integration project.",
    nextStep: "Pick your single highest-pain data hand-off (usually BOM or work-order release) and target L1 for just that one flow.",
    effortToReachNext: "3–6 months · $50–150K",
  },
  1: {
    oneLiner: "A few connections exist, but each was hand-built and fragile.",
    plainEnglish: "You've wired up one or two integrations — maybe a nightly file export from ERP, or a custom script that pushes BOMs. They work, but each one is a one-off. When something breaks, only one person knows how to fix it.",
    chooseWhen: [
      "You have 1–2 working integrations but no repeatable pattern",
      "Integrations are scheduled file transfers (CSV/FTP) or custom scripts",
      "You're starting your first 'real' integration project",
      "You want to establish the process-engineering foundation (EasyPlan) first",
    ],
    avoidWhen: [
      "You already run vendor gateways or an integration platform",
      "You need real-time (sub-minute) data flow — batch won't cut it",
      "Multiple teams already depend on documented SLAs",
    ],
    realWorldSignals: [
      "\"The integration\" is a cron job someone set up two years ago",
      "Data is fresh once a night, not in real time",
      "When the file transfer fails, you find out the next morning",
      "Only one engineer understands how the connection works",
    ],
    whatYouGet: "The EasyPlan process-engineering group comes into scope. You can track, plan and risk-assess your foundational interfaces — but the heavy gateway integrations stay locked until L2.",
    typicalOrg: "A manufacturer that has dabbled in integration and is now committing to do it properly.",
    nextStep: "Stand up your first vendor gateway (CN4T for PLM↔MES, or CN4S for MES↔ERP) and document SLAs. That's L2.",
    effortToReachNext: "6–9 months · $400–800K",
  },
  2: {
    oneLiner: "We have a repeatable integration pattern with clear ownership.",
    plainEnglish: "You've adopted a real integration approach — vendor gateways (CN4T, CN4S, AIG) or an iPaaS hub. New integrations follow a known recipe instead of being invented from scratch. Someone owns each connection and there are basic SLAs.",
    chooseWhen: [
      "You run at least one vendor gateway (CN4T / CN4S / AIG) or an iPaaS",
      "There's a canonical-ish data model emerging",
      "SLAs are documented (even if not yet measured automatically)",
      "You're connecting the core three-tier systems (PLM, MES, ERP)",
    ],
    avoidWhen: [
      "You only have hand-built scripts (that's L1)",
      "All three tiers are already live with enforced canonical contracts (that's L3)",
      "You need event-driven, measured pipelines (that's L4)",
    ],
    realWorldSignals: [
      "\"We use CN4T for that\" is a sentence people say",
      "There's a named owner for each integration",
      "You have a mapping document, version-controlled",
      "Failures land in a dead-letter queue, not a black hole",
    ],
    whatYouGet: "The core gateway groups unlock — CN4T, CN4S, AIG, plus PLM and MES. Protocols, Architecture, LLD, Intelligence and Taxonomy tabs all open. This is where the platform becomes genuinely useful day-to-day.",
    typicalOrg: "Mid-market manufacturer mid-way through a CLM rollout, or a plant with one tier-bridge live.",
    nextStep: "Get all three tiers (PLM↔MES↔ERP) live with gateways enforcing canonical models, documented SLAs, and shop-floor + quality in scope. That's L3 — the classic CLM go-live.",
    effortToReachNext: "12 months · $1.5–3M",
  },
  3: {
    oneLiner: "Full three-tier CLM, standardized, with canonical contracts. The sweet spot.",
    plainEnglish: "PLM, MES and ERP are all connected through gateways that enforce canonical data models. Changes flow automatically across all three tiers. SLAs are documented and the shop floor and quality lab are integrated. This is what most successful CLM programmes look like at go-live.",
    chooseWhen: [
      "All three tiers (PLM↔MES↔ERP) are integrated or in active rollout",
      "Gateways enforce canonical models — not ad-hoc field mapping",
      "Shop-floor (SFC) and quality (QM) are in or entering scope",
      "You're a discrete manufacturer doing a standard CLM go-live",
    ],
    avoidWhen: [
      "Only one or two tiers are connected (that's L2)",
      "You need event-driven streaming + measured pipelines (that's L4)",
      "Your industry mandates B2MML/ISA-95 federated XML (go L4)",
    ],
    realWorldSignals: [
      "An ECO released in PLM appears on the line without anyone re-keying it",
      "You can name your canonical objects and who owns each",
      "Quality results flow back to ERP automatically",
      "SLAs exist and people are held to them",
    ],
    whatYouGet: "The full strategic surface: Master Architecture, E2E Gaps, Data Contracts and Benefits all unlock. This is the recommended default — vendor-supported, well-understood, ~12-month proven path.",
    typicalOrg: "The 80% case for discrete-manufacturing CLM. Automotive, aerospace, industrial-equipment makers at go-live.",
    nextStep: "Move to event-driven (Kafka) or B2MML/ISA-95 federation, with measured latency/error-rate/completeness. That's L4 — for scale or regulatory needs.",
    effortToReachNext: "18 months · $3–6M",
  },
  4: {
    oneLiner: "Event-driven and/or federated, with everything measured.",
    plainEnglish: "You've moved beyond point-to-point gateways to an event-driven backbone (Kafka) or a formal B2MML/ISA-95 federation. Every flow is measured — latency, error rate, completeness. Schema changes are governed by a registry. This is enterprise-grade.",
    chooseWhen: [
      "You run an event log (Kafka) or B2MML/ISA-95 federation",
      "Latency, error-rate and completeness are measured automatically",
      "A schema registry governs contract evolution",
      "You're regulated (pharma, aero, medical) and need canonical XML envelopes",
    ],
    avoidWhen: [
      "You're still on synchronous point-to-point gateways (that's L3)",
      "You haven't yet closed the three-tier loop (go L3 first — don't skip)",
      "You want autonomous AI agents in the loop (that's L5)",
    ],
    realWorldSignals: [
      "You have a Grafana dashboard of integration health",
      "\"Schema registry\" and \"BACKWARD compatibility\" are in your vocabulary",
      "Failed messages are replayed from the event log, not lost",
      "You can prove completeness, not just hope for it",
    ],
    whatYouGet: "ESB, DFD and B2MML Federation tabs unlock, plus the Edge, Insights Hub and MDG interface groups. All 56 interfaces come into scope. The federated architecture surfaces become live.",
    typicalOrg: "Global enterprise with 50+ interfaces, or any regulated manufacturer requiring audit-grade traceability.",
    nextStep: "Add autonomous agents, data mesh, or a digital thread/twin with closed-loop feedback. That's L5 — transformative.",
    effortToReachNext: "12–36 months · $8–20M+",
  },
  5: {
    oneLiner: "The loop closes itself — AI agents, data mesh, or digital twin.",
    plainEnglish: "Your CLM substrate is now a platform that improves itself. AI agents file ECNs, schedule jobs and raise NCRs under human-in-loop gates. Or you've built a data mesh of domain-owned products, or a full digital thread with simulation in the loop. Few organisations are genuinely here.",
    chooseWhen: [
      "You have autonomous agents acting on the CLM substrate (with guardrails)",
      "You run a data mesh of domain-owned data products",
      "You have a digital thread/twin with simulation feedback",
      "Continuous improvement is automated, not a quarterly project",
    ],
    avoidWhen: [
      "You're describing an aspiration, not a running capability (be honest — pick L4)",
      "Your organisational maturity (not just tech) isn't genuinely here",
      "You haven't yet measured your L4 pipelines reliably",
    ],
    realWorldSignals: [
      "Agents propose changes; humans approve at defined gates",
      "You can replay any product's full lifecycle from idea to scrap",
      "Anomalies trigger autonomous remediation, logged for audit",
      "Data products have owners, SLAs and contracts",
    ],
    whatYouGet: "Everything unlocks, including the Agentic AI tab. The full transformative surface — every tab, every interface group, every capability flag enabled.",
    typicalOrg: "Cutting-edge enterprises (2025+). Most who claim L5 actually run L3/L4 with L5 marketing — be rigorous.",
    nextStep: "You're at the frontier. Focus on proving ROI, governance and audit-trail integrity for autonomous actions.",
    effortToReachNext: "Continuous — this is a capability, not a destination.",
  },
};

// ─── 2 · COMPLEXITY LEVELS ──────────────────────────────────────────────────
export const COMPLEXITY_LEVELS = {
  1: { id: 1, key: "C1", label: "Very Low", color: "#00ff88",
       description: "1 plant · <100 SKUs · 1–3 systems · no regulatory burden." },
  2: { id: 2, key: "C2", label: "Low",      color: "#00d4c0",
       description: "1–2 plants · a handful of systems · light compliance." },
  3: { id: 3, key: "C3", label: "Medium",   color: "#ffb300",
       description: "Multi-plant · 5–10 systems · documented quality processes." },
  4: { id: 4, key: "C4", label: "High",     color: "#fb923c",
       description: "Global · 10+ systems · regulated industry · 40+ interfaces." },
  5: { id: 5, key: "C5", label: "Very High",color: "#ff4444",
       description: "Enterprise · 50+ interfaces · multi-regulatory · digital thread ambitions." },
};

// ─── 2b · COMPLEXITY GUIDANCE (what each driver actually means) ─────────────
export const COMPLEXITY_GUIDANCE = {
  1: { plainEnglish: "One site, few products, almost no compliance burden. The simplest possible environment.",
       drivers: ["1 plant / site", "<100 active products", "1–3 systems total", "No regulatory requirements"] },
  2: { plainEnglish: "A small footprint with a handful of systems and light compliance — e.g. basic ISO.",
       drivers: ["1–2 plants", "A few systems", "Light compliance (ISO 9001)", "Modest product variety"] },
  3: { plainEnglish: "Multiple plants, a real system landscape, and documented quality processes.",
       drivers: ["Multi-plant", "5–10 systems", "Documented quality processes", "Moderate product variety"] },
  4: { plainEnglish: "Global operations, a large system estate, regulated industry, and 40+ interfaces to manage.",
       drivers: ["Global / multi-region", "10+ systems", "Regulated industry", "40+ interfaces"] },
  5: { plainEnglish: "Enterprise scale with 50+ interfaces, multiple regulatory regimes, and digital-thread ambitions.",
       drivers: ["Enterprise scale", "50+ interfaces", "Multi-regulatory", "Digital-thread / twin ambitions"] },
};

// ─── 2c · SELF-ASSESSMENT QUIZ ─────────────────────────────────────────────
// Five yes/no questions that infer a maturity level for users who aren't sure.
// Each "yes" that's answered pushes the inferred maturity up to that question's
// level. The highest "yes" wins. This removes guesswork for non-experts.
//
export const MATURITY_QUIZ = [
  { id: "q1", level: 1, text: "Do you have at least one working automated data connection between two systems (even a nightly file transfer)?",
    yesMeans: "You're at least L1 — past pure manual.", noMeans: "You're at L0 — everything is manual." },
  { id: "q2", level: 2, text: "Do you run a vendor integration gateway (CN4T, CN4S, AIG) or an integration platform (MuleSoft, Boomi), with a named owner per connection?",
    yesMeans: "You're at least L2 — you have a repeatable pattern.", noMeans: "You're below L2 — connections are still one-offs." },
  { id: "q3", level: 3, text: "Are all three tiers — PLM, MES and ERP — connected through gateways that enforce a canonical data model, with documented SLAs?",
    yesMeans: "You're at least L3 — the standard CLM sweet spot.", noMeans: "You're below L3 — the three-tier loop isn't fully closed." },
  { id: "q4", level: 4, text: "Is your integration event-driven (Kafka) or B2MML/ISA-95 federated, with latency and error-rate measured automatically via a schema registry?",
    yesMeans: "You're at least L4 — measured and federated.", noMeans: "You're below L4 — still synchronous / unmeasured." },
  { id: "q5", level: 5, text: "Do autonomous agents (or a data mesh / digital twin) act on your CLM data under human-in-loop gates, improving the loop continuously?",
    yesMeans: "You're at L5 — the transformative frontier.", noMeans: "You're below L5 — no autonomous closed loop yet." },
];

// Infer maturity from an answers object { q1: true/false, ... }
export function inferMaturityFromQuiz(answers) {
  let level = 0;
  for (const q of MATURITY_QUIZ) {
    if (answers[q.id]) level = Math.max(level, q.level);
    else break; // maturity is a ladder — stop at first "no"
  }
  return level;
}

// ─── 3 · ARCHITECTURE OPTIONS (the 15-row matrix) ──────────────────────────
export const ARCHITECTURE_OPTIONS = [
  { n: 1,  name: "Spreadsheet + Email",            band: "NO CLM",          maturity: 0, complexity: 1, topology: "None — humans relay data", ttv: "Immediate", cost: "$0" },
  { n: 2,  name: "Single-system silos",            band: "NO CLM",          maturity: 0, complexity: 1, topology: "Isolated PLM + ERP + MES", ttv: "Existing", cost: "$0" },
  { n: 3,  name: "CSV / FTP batch exchange",       band: "NO CLM",          maturity: 1, complexity: 2, topology: "Hub-less scheduled file drops", ttv: "3–6 months", cost: "$50–150K" },
  { n: 4,  name: "Point-to-point (P2P)",           band: "TACTICAL CLM",    maturity: 1, complexity: 3, topology: "N-to-N adapters (spaghetti)", ttv: "4–8 months", cost: "$200–500K" },
  { n: 5,  name: "PLM↔MES only (CN4T/PDX)",        band: "TACTICAL CLM",    maturity: 2, complexity: 3, topology: "Single tier-bridge", ttv: "6 months", cost: "$400–800K" },
  { n: 6,  name: "PLM↔ERP only (AIG/T4S)",         band: "TACTICAL CLM",    maturity: 2, complexity: 3, topology: "Single tier-bridge", ttv: "6 months", cost: "$400–800K" },
  { n: 7,  name: "Hub-and-spoke (ETL / iPaaS)",    band: "TACTICAL CLM",    maturity: 2, complexity: 3, topology: "Central broker (MuleSoft/Boomi)", ttv: "9 months", cost: "$800K–1.5M" },
  { n: 8,  name: "Three-tier CLM (PLM↔MES↔ERP)",   band: "STRATEGIC CLM",   maturity: 3, complexity: 4, topology: "Star with 3 vendor gateways", ttv: "12 months", cost: "$1.5–3M" },
  { n: 9,  name: "ESB-mediated CLM (SOAP/REST)",   band: "STRATEGIC CLM",   maturity: 3, complexity: 4, topology: "Bus — all flows through ESB", ttv: "12–18 months", cost: "$2–4M" },
  { n: 10, name: "Event-driven CLM (Kafka)",       band: "STRATEGIC CLM",   maturity: 4, complexity: 4, topology: "Distributed event log, fan-out", ttv: "18 months", cost: "$3–5M" },
  { n: 11, name: "B2MML / ISA-95 federated",       band: "STRATEGIC CLM",   maturity: 4, complexity: 5, topology: "Layered federation L4↔L3↔L2↔L1", ttv: "18 months", cost: "$4–6M" },
  { n: 12, name: "Microservices + domain events",  band: "TRANSFORMATIVE",  maturity: 4, complexity: 5, topology: "Service mesh, DDD boundaries", ttv: "24+ months", cost: "$5–10M" },
  { n: 13, name: "Data mesh / data fabric",        band: "TRANSFORMATIVE",  maturity: 5, complexity: 5, topology: "Decentralized data products", ttv: "18–36 months", cost: "$6–12M" },
  { n: 14, name: "Agentic AI orchestration",       band: "TRANSFORMATIVE",  maturity: 5, complexity: 5, topology: "LLM agents over CLM substrate", ttv: "12–36 months", cost: "$8–15M" },
  { n: 15, name: "Digital thread + digital twin",  band: "TRANSFORMATIVE",  maturity: 5, complexity: 5, topology: "Thread + twin + simulation loop", ttv: "24+ months", cost: "$10–20M+" },
];

// Band accent colours
export const BAND_META = {
  "NO CLM":        { color: "#64b5d6", icon: "🚫" },
  "TACTICAL CLM":  { color: "#ffb300", icon: "🔧" },
  "STRATEGIC CLM": { color: "#00e5ff", icon: "🎯" },
  "TRANSFORMATIVE":{ color: "#ff4f8b", icon: "🚀" },
};

// ─── 4 · TAB CATALOG with maturity gating ──────────────────────────────────
// Each tab declares the MINIMUM maturity level at which it becomes meaningful.
// `core: true` tabs are always shown (you can always track and export).
//
export const TAB_CATALOG = [
  { id: "config",    label: "⚙️ Maturity Setup",     minMaturity: 0, core: true,  always: true },
  { id: "table",     label: "📋 Tracker",            minMaturity: 0, core: true },
  { id: "kanban",    label: "📌 Kanban",             minMaturity: 0, core: true },
  { id: "export",    label: "📤 Export",             minMaturity: 0, core: true },
  { id: "risk",      label: "⚠️ Risk",               minMaturity: 1 },
  { id: "timeline",  label: "📅 Timeline",           minMaturity: 1 },
  { id: "plan",      label: "🏗 Implementation",     minMaturity: 1 },
  { id: "protocols", label: "🔌 Protocols",          minMaturity: 2 },
  { id: "visual",    label: "🏗️ Architecture",       minMaturity: 2 },
  { id: "syncGuide", label: "👥 Sync Guide",         minMaturity: 2 },
  { id: "lld",       label: "📐 LLD",                minMaturity: 2 },
  { id: "intel",     label: "🧠 Intelligence",       minMaturity: 2 },
  { id: "taxonomy",  label: "🗂 Taxonomy",           minMaturity: 2 },
  { id: "master",    label: "🗺️ Master Arch",        minMaturity: 3 },
  { id: "e2e",       label: "🔗 E2E Gaps",           minMaturity: 3 },
  { id: "contracts", label: "📋 Contracts",          minMaturity: 3 },
  { id: "benefits",  label: "🎯 Benefits",           minMaturity: 3 },
  { id: "esb",       label: "🚌 ESB",                minMaturity: 4 },
  { id: "dfd",       label: "🔄 DFD",                minMaturity: 4 },
  { id: "b2mml",     label: "🏛 B2MML Federation",   minMaturity: 4 },
  { id: "agentic",   label: "🤖 Agentic AI",         minMaturity: 5 },
];

// ─── 5 · INTERFACE GROUP scope by maturity ─────────────────────────────────
// Lower maturity programmes only have a few integration groups in scope.
// As maturity climbs, more groups unlock (shop-floor, edge, analytics, MDG).
//
export const GROUP_SCOPE_BY_MATURITY = {
  0: [],                                                       // no integrations at all
  1: ["EP"],                                                   // process-engineering foundation only
  2: ["EP", "CN4T", "CN4S", "AIG", "PLM", "MES"],              // core three-tier gateways
  3: ["EP", "CN4T", "CN4S", "AIG", "PLM", "MES", "SFC", "QUAL"], // + shop floor + quality
  4: ["EP", "CN4T", "CN4S", "AIG", "PLM", "MES", "SFC", "QUAL", "EDGE", "HUB", "MDG"], // + edge + analytics + MDG
  5: ["EP", "CN4T", "CN4S", "AIG", "PLM", "MES", "SFC", "QUAL", "EDGE", "HUB", "MDG"], // everything
};

// ─── 6 · CAPABILITY FLAGS by maturity ──────────────────────────────────────
// Features that turn on/off depending on maturity.
//
export const CAPABILITIES_BY_MATURITY = {
  0: { canonical: false, eventDriven: false, federation: false, agentic: false, sla: false, monitoring: false, dlq: false, audit: false },
  1: { canonical: false, eventDriven: false, federation: false, agentic: false, sla: false, monitoring: false, dlq: false, audit: false },
  2: { canonical: true,  eventDriven: false, federation: false, agentic: false, sla: true,  monitoring: false, dlq: true,  audit: false },
  3: { canonical: true,  eventDriven: false, federation: false, agentic: false, sla: true,  monitoring: true,  dlq: true,  audit: true  },
  4: { canonical: true,  eventDriven: true,  federation: true,  agentic: false, sla: true,  monitoring: true,  dlq: true,  audit: true  },
  5: { canonical: true,  eventDriven: true,  federation: true,  agentic: true,  sla: true,  monitoring: true,  dlq: true,  audit: true  },
};

export const CAPABILITY_LABELS = {
  canonical:   "Canonical data model",
  eventDriven: "Event-driven (pub-sub)",
  federation:  "B2MML / ISA-95 federation",
  agentic:     "Agentic AI orchestration",
  sla:         "SLA tracking",
  monitoring:  "Real-time monitoring",
  dlq:         "Dead-letter queues + retry",
  audit:       "Audit ledger / SIEM",
};

// ─── 7 · THE RESOLVER — (maturity, complexity) → full profile ──────────────
// Picks the best-fit architecture option for the chosen pair and assembles
// the application profile the UI will consume.
//
export function resolveProfile(maturity, complexity) {
  const m = Number(maturity);
  const c = Number(complexity);

  // Find candidate architecture options: prefer exact maturity match, then
  // closest complexity. Fall back to nearest maturity if none exact.
  const exactM = ARCHITECTURE_OPTIONS.filter(o => o.maturity === m);
  let pool = exactM.length ? exactM : ARCHITECTURE_OPTIONS;
  // pick option whose complexity is closest to requested
  let best = pool[0];
  let bestDist = Infinity;
  for (const o of pool) {
    const dist = Math.abs(o.complexity - c) + Math.abs(o.maturity - m) * 0.5;
    if (dist < bestDist) { bestDist = dist; best = o; }
  }

  // Visible tabs: every tab whose minMaturity <= m (plus always-on tabs)
  const visibleTabs = TAB_CATALOG
    .filter(t => t.always || t.minMaturity <= m)
    .map(t => t.id);

  // Locked tabs (shown greyed in config so user sees what unlocking gains)
  const lockedTabs = TAB_CATALOG
    .filter(t => !t.always && t.minMaturity > m)
    .map(t => ({ id: t.id, label: t.label, unlocksAt: t.minMaturity }));

  const groupsInScope = GROUP_SCOPE_BY_MATURITY[m] || [];
  const capabilities = CAPABILITIES_BY_MATURITY[m] || CAPABILITIES_BY_MATURITY[0];

  return {
    maturity: m,
    complexity: c,
    maturityMeta: MATURITY_LEVELS[m],
    complexityMeta: COMPLEXITY_LEVELS[c],
    architecture: best,
    band: best.band,
    bandMeta: BAND_META[best.band],
    visibleTabs,
    lockedTabs,
    groupsInScope,
    capabilities,
    accent: MATURITY_LEVELS[m]?.color || "#00e5ff",
  };
}

// ─── 8 · Recommendation helper (advice text) ───────────────────────────────
export function getProfileAdvice(maturity, complexity) {
  const m = Number(maturity), c = Number(complexity);
  // Maturity/complexity mismatch warnings
  if (m === 0 && c >= 4) {
    return { tone: "warning", text: "High complexity at zero maturity is the classic failure pattern. Build managed integrations (L2) before attempting global scope. Don't skip the ladder." };
  }
  if (m >= 4 && c <= 2) {
    return { tone: "info", text: "Event-driven/federated maturity for low-complexity scope may be over-engineering. Confirm the operational cost is justified by future scale." };
  }
  if (m >= 1 && c - m >= 2) {
    return { tone: "warning", text: "Your target complexity outpaces your maturity by 2+ levels. Expect the project to land one tier lower than planned. Sequence the maturity climb explicitly." };
  }
  if (m === 3 && c === 4) {
    return { tone: "success", text: "This is the sweet spot for 80% of discrete-manufacturing CLM programmes — three-tier vendor-gateway CLM. Well-understood, vendor-supported, ~12-month go-live." };
  }
  if (m === 4 && c === 5) {
    return { tone: "success", text: "B2MML/ISA-95 federation — the right target for regulated industries (pharma, aero, medical). Budget for XSD governance discipline." };
  }
  if (m === 5) {
    return { tone: "info", text: "Transformative tier. Most enterprises talk about this but execute as L3/L4. Make sure the organisational maturity (not just tech) is genuinely here." };
  }
  return { tone: "neutral", text: MATURITY_LEVELS[m]?.description || "" };
}

// ─── 9 · Default profile ───────────────────────────────────────────────────
// The app's out-of-box default: L3 / C4 (the most common real-world CLM target).
export const DEFAULT_PROFILE = { maturity: 3, complexity: 4 };
